import React, { useCallback, useEffect, useState } from "react";
import styles from "./styles.module.scss";
import PagePanel from "app/components/PagePanel";
import Button from "app/components/Button/ButtonVariant";
import GettingStartedProgress from "app/components/GettingStarted/GettingStartedProgress";
// import UserSelect from "app/components/Filter/UserSelect";
import UserModel from "app/models/UserModel";
import SelectBox, { UserSelect } from "app/components/inputs/SelectBox";
import UserAvatar from "app/components/UserAvatar";
import { cls } from "app/utils";
import { useActions } from "app/utils/hooks";
import {
  addSegmentMember,
  clearSegments,
  fetchSegmentById,
} from "app/actions/segments";
import { useHistory, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

interface RouteParams {
  id: string;
}

const InviteToSegment = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { id } = useParams<RouteParams>();
  const [error, setError] = useState(false);
  const [selectedRole, setSelectedRole] = useState<{
    value: string;
    label: string;
  }>({ value: "coach", label: t("Coach") });
  const [selectedUser, setSelectedUser] = useState<{ model: UserModel } | null>(
    null,
  );
  const [loading, setLoading] = useState(false);
  const cancelUrl = id ? `/lists/${id}/spaces` : "/lists/";

  const segmentActions = useActions({
    addSegmentMember,
    clearSegments,
    fetchSegmentById,
  });

  // The global breadcrumb names the list from segments.segmentsData, which
  // this page doesn't otherwise load — fetch it so the trail can link back to
  // the list, and clear on unmount like PostToSegment does.
  useEffect(() => {
    if (id) {
      segmentActions.fetchSegmentById(id);
    }

    return () => segmentActions.clearSegments();
  }, [id]);

  const onUserSelect = useCallback(
    (selectedData: { model: UserModel }) => {
      if (selectedData) {
        if (Array.isArray(selectedData)) {
          setSelectedUser(selectedData?.[0]);
        } else {
          setSelectedUser(selectedData);
        }
        setError(false);
      }
    },
    [selectedUser],
  );
  const onCancel = () => {
    history.push(cancelUrl);
  };

  const updateHandler = async () => {
    if (!selectedUser) {
      return setError(true);
    }

    setError(false);
    setLoading(true);
    try {
      const resp = await segmentActions.addSegmentMember(id, {
        email: selectedUser?.model?.email,
        name: selectedUser?.model?.displayName,
        role: selectedRole?.value,
      });
      if (resp) {
        history.push(cancelUrl);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const onRoleSelect = (e: any) => {
    setSelectedRole(e);
  };

  const onRemoveUserClick = () => {
    setSelectedUser(null);
  };

  const SelectedUser = useCallback(() => {
    return (
      <div className={styles.user_container}>
        <div className={styles.user_info_container}>
          <UserAvatar
            className={styles.avatar}
            sizeWidth={44}
            sizeHeight={44}
            altName="author"
            userName={selectedUser.model.name}
            srcName={selectedUser.model.avatar as string}
          />
          <div className={styles.user_info}>
            <h4>{selectedUser.model.displayName}</h4>
            <p>{selectedUser.model.email}</p>
          </div>
        </div>
        <Button
          title={<i className={cls("ico ico-x text-danger")} />}
          onClickFunc={onRemoveUserClick}
          buttonType="secondary-outline"
        />
      </div>
    );
  }, [selectedUser]);

  return (
    <>
      <GettingStartedProgress />
      <div className={styles.container}>
        <PagePanel title={t("Invite member to all Spaces in the List")} />
        <div className={styles.content_wrapper}>
          <span className={styles.input_wrapper}>
            <label htmlFor="segment_name">{t("User")}</label>
            {!selectedUser && (
              <UserSelect
                isCreatable
                no-options-text={t("Add new user")}
                onChange={(e: any) => onUserSelect(e)}
                hideSelected
                isMulti={false}
                className={styles.user_list_input}
                value={selectedUser}
              />
            )}
            {selectedUser && selectedUser.model && <SelectedUser />}
          </span>
          <span className={styles.input_wrapper}>
            <label htmlFor="segment_name">{t("Role")}</label>
            <SelectBox
              onChange={onRoleSelect}
              className={styles.user_list_input}
              options={[
                { value: "coach", label: t("Coach") },
                { value: "athlete", label: t("Athlete") },
                { value: "member", label: t("Member") },
              ]}
              value={selectedRole}
            />
          </span>
          <span className={styles.btn_wrapper}>
            <Button
              title={t("Add")}
              buttonType="primary"
              disabled={error || !selectedUser}
              onClickFunc={updateHandler}
              loading={loading}
              buttonStyles={styles.add_button}
            />
            <div onClick={onCancel}>
              <Button title={t("Cancel")} buttonType="secondary-outline" />
            </div>
          </span>
        </div>
      </div>
    </>
  );
};

export default InviteToSegment;
